import type { ConfigResponse, EmailsResponse } from "@site-alerter/shared";
import { appConfig } from "./env.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: { error?: string; details?: string[]; current?: ConfigResponse },
  ) {
    super(payload.error ?? `request failed with ${status}`);
    this.name = "ApiError";
  }
}

/**
 * The token must be the **ID** token. Cognito access tokens carry `client_id`
 * rather than `aud`, so the API Gateway JWT authorizer — whose audience is the
 * app client ID — rejects them with a 401 that looks like a broken authorizer
 * (SPEC §7).
 */
async function request<T>(idToken: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${appConfig.apiUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.body != null ? { "content-type": "application/json" } : {}),
      ...init?.headers,
      authorization: `Bearer ${idToken}`,
    },
  });

  const text = await response.text();
  const payload = text === "" ? {} : (JSON.parse(text) as Record<string, unknown>);

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

export function getConfig(idToken: string): Promise<ConfigResponse> {
  return request<ConfigResponse>(idToken, "/config");
}

export function putConfig(
  idToken: string,
  recipients: string[],
  version: number,
): Promise<ConfigResponse> {
  return request<ConfigResponse>(idToken, "/config", {
    method: "PUT",
    body: JSON.stringify({ recipients, version }),
  });
}

export function postPause(
  idToken: string,
  enabled: boolean,
  pausedUntil: string | null,
): Promise<ConfigResponse> {
  return request<ConfigResponse>(idToken, "/pause", {
    method: "POST",
    body: JSON.stringify({ enabled, pausedUntil }),
  });
}

export function getEmails(
  idToken: string,
  days: number,
  cursor: string | null,
): Promise<EmailsResponse> {
  const params = new URLSearchParams({ days: String(days) });
  if (cursor != null) params.set("cursor", cursor);
  return request<EmailsResponse>(idToken, `/emails?${params.toString()}`);
}
